import { ChangeEvent, CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Loader2, Pencil, Plus, Printer, Search, Share2, Trash2, Undo2, X } from 'lucide-react';
import QRCode from 'qrcode';
import { buildPublicQrUrl } from '../../utils/publicOrigin';
import { lettersApi } from '../../services/api';
import { buildLetterRouteUrl } from '../../utils/letterRoutes';
import { useAuth } from '../../context/AuthContext';
import { LetterDocument } from './LetterDocument';
import { NewLetterEditor } from './new-editor/NewLetterEditor';
import type { PdfDownloaderFn } from './new-editor/components/PreviewPane';
import { extractSignaturePlacementFromBody } from './signatureLayoutShared';
import { formatSeplReferenceNumber } from '../../utils/helpers';
import { formatISTDateTime } from '../../utils/dateTime';
import * as pdfjsLib from 'pdfjs-dist';
// Bundle the pdf.js worker through Vite (hashed, same-origin) so printing can
// rasterise the generated PDF to page images without the fake-worker crash.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Off-screen host for the editor-accurate PDF renderer. Must use REAL layout
// (not display:none) so letterhead/footer images load and the editor can
// measure page count / header-footer heights; positioned far off-screen and
// non-interactive so the user never sees it.
const OFFSCREEN_EDITOR_HOST_STYLE: CSSProperties = {
  position: 'fixed',
  left: '-99999px',
  top: 0,
  // Match the live editor's real width (CreateLetterPage renders it wide) so the
  // preview pane is wider than the letter page and PreviewPane measures the
  // letterhead/footer/page sizes identically to the on-screen editor.
  width: '1800px',
  height: '1400px',
  opacity: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  zIndex: -1,
};

// Wait until a PreviewPane has registered its exporter, then allow a short
// settle so letterhead/footer images load and pagination commits before we
// serialise the document to a Blob.
const waitForPdfDownloader = async (
  ref: React.MutableRefObject<PdfDownloaderFn | null>,
  settleMs = 900,
): Promise<PdfDownloaderFn> => {
  const deadline = Date.now() + 5000;
  while (!ref.current) {
    if (Date.now() > deadline) {
      throw new Error('Editor renderer did not initialise in time.');
    }
    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
  }
  await new Promise((resolve) => setTimeout(resolve, settleMs));
  if (!ref.current) {
    throw new Error('Editor renderer was torn down before export.');
  }
  return ref.current;
};

const noopEditorChange = () => {};


// Format ISO timestamp to dd-mm-yyyy hh:mm AM/PM format
const formatDateTime = (dateString: string | null | undefined): string => {
  return formatISTDateTime(dateString);
};

interface LetterPdfUpload {
  id: number;
  letter_id: number;
  file_name: string;
  file_mime?: string | null;
  file_size?: number | null;
  uploaded_at?: string | null;
  uploaded_by?: number;
  uploaded_by_name?: string;
}

interface LetterSummary {
  id: number;
  serial_number: number;
  letter_number: string;
  subject: string;
  letter_date: string;
  file_name?: string;
  mapped_pdf_name?: string | null;
  mapped_pdf_path?: string | null;
  mapped_pdf_mime?: string | null;
  mapped_pdf_size?: number | null;
  mapped_pdf_uploaded_by?: number | null;
  mapped_pdf_uploaded_at?: string | null;
  upload_count?: number;
  department?: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  approved_at?: string | null;
  approved_by?: number | null;
  approved_signature_html?: string | null;
  created_at: string;
  updated_at: string;
  last_saved_at: string;
  created_by: number;
  created_by_name: string;
  created_by_email: string;
  updated_by_name: string;
  tags?: string[] | string | null;
  rejection_remark?: string | null;
}

interface LetterAudit {
  id: number;
  action: string;
  details: string;
  created_at: string;
  user_name?: string;
  user_email?: string;
}

interface LetterDetail extends LetterSummary {
  body_html: string;
  letterhead_html: string;
  footer_html: string;
  approved_signature_html?: string | null;
  qr_code_data?: string | null;
  qr_code_token?: string | null;
  file_name?: string;
  pdf_uploads?: LetterPdfUpload[];
  auditLogs: LetterAudit[];
}

interface ViewLettersPageProps {
  defaultStatus?: string;
  pageTitle?: string;
}

const buildFallbackUploads = (letter?: Partial<LetterDetail> | null): LetterPdfUpload[] => {
  if (!letter?.id || !letter?.mapped_pdf_name) return [];

  return [{
    id: -Number(letter.id),
    letter_id: Number(letter.id),
    file_name: String(letter.mapped_pdf_name),
    file_mime: letter.mapped_pdf_mime || 'application/pdf',
    file_size: letter.mapped_pdf_size ?? null,
    uploaded_at: letter.mapped_pdf_uploaded_at || null,
    uploaded_by: Number(letter.mapped_pdf_uploaded_by || 0) || undefined,
    uploaded_by_name: undefined,
  }];
};

const normalizeLetterDetailUploads = (detail: LetterDetail, uploadsFromApi?: LetterPdfUpload[] | null): LetterDetail => {
  const detailUploads = Array.isArray(detail?.pdf_uploads) ? detail.pdf_uploads : [];
  const apiUploads = Array.isArray(uploadsFromApi) ? uploadsFromApi : [];
  const fallbackUploads = buildFallbackUploads(detail);
  const mergedUploads = apiUploads.length > 0
    ? apiUploads
    : detailUploads.length > 0
      ? detailUploads
      : fallbackUploads;

  return {
    ...detail,
    pdf_uploads: mergedUploads,
    upload_count: typeof detail?.upload_count === 'number'
      ? Math.max(detail.upload_count, mergedUploads.length)
      : mergedUploads.length,
  };
};

type DateRangePreset = 'last_7_days' | 'last_month' | 'all_time' | 'custom';

const statusLabelMap: Record<LetterSummary['status'], string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

const statusClassMap: Record<LetterSummary['status'], string> = {
  draft: 'bg-amber-100 text-amber-700',
  pending_approval: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

const formatRejectionRemark = (value?: string | null) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const match = text.match(/Remark:\s*(.*)$/i);
  return (match?.[1] || text).trim();
};

const getDetailRejectionRemark = (letter?: LetterDetail | null) => {
  if (!letter) return '';
  const direct = formatRejectionRemark(letter.rejection_remark);
  if (direct) return direct;
  const auditRemark = letter.auditLogs?.find((log) => log.action === 'rejected')?.details;
  return formatRejectionRemark(auditRemark);
};

export function ViewLettersPage({ defaultStatus = '', pageTitle = 'View Letters' }: ViewLettersPageProps = {}) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDraftsPage = defaultStatus === 'draft' || pageTitle === 'View Drafts';

  const [letters, setLetters] = useState<LetterSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedLetter, setSelectedLetter] = useState<LetterDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [accessPopupMessage, setAccessPopupMessage] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [shareError, setShareError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [downloadingLetterId, setDownloadingLetterId] = useState<number | null>(null);
  const [printingLetterId, setPrintingLetterId] = useState<number | null>(null);
  const [sharingLetterId, setSharingLetterId] = useState<number | null>(null);
  const [uploadingLetterId, setUploadingLetterId] = useState<number | null>(null);
  const [openDownloadMenuId, setOpenDownloadMenuId] = useState<number | null>(null);
  const [downloadMenuPos, setDownloadMenuPos] = useState<{ top: number; left: number } | null>(null);
  const downloadBtnRef = useRef<HTMLButtonElement | null>(null);
  const [uploadTargetLetterId, setUploadTargetLetterId] = useState<number | null>(null);
  const [pdfRenderData, setPdfRenderData] = useState<LetterDetail | null>(null);
  const [activeUploadTabId, setActiveUploadTabId] = useState<number | null>(null);
  const [activeUploadPreviewUrl, setActiveUploadPreviewUrl] = useState<string>('');
  const [activeUploadPreviewLoading, setActiveUploadPreviewLoading] = useState(false);
  const [activeUploadPreviewError, setActiveUploadPreviewError] = useState('');
  const [selectedLetterQrFallback, setSelectedLetterQrFallback] = useState<string | null>(null);
  const pdfRenderRef = useRef<HTMLDivElement>(null);
  // Registered by the off-screen NewLetterEditor that renders downloads/share
  // PDFs through the SAME engine the draft editor uses (so tables, alignment
  // and signature placement match exactly). Driven by `pdfRenderData`.
  const viewerPdfDownloaderRef = useRef<PdfDownloaderFn | null>(null);
  // Preview-modal editor-accurate render (shown as a PDF in an iframe so the
  // preview is byte-identical to the download). Driven by `previewRenderData`.
  const [previewRenderData, setPreviewRenderData] = useState<LetterDetail | null>(null);
  const previewPdfDownloaderRef = useRef<PdfDownloaderFn | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('');
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);
  const [previewPdfFailed, setPreviewPdfFailed] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [datePreset, setDatePreset] = useState<DateRangePreset>('all_time');
  const [advancedSearchEnabled, setAdvancedSearchEnabled] = useState(false);
  const [filters, setFilters] = useState({ search: '', fromDate: '', toDate: '', userId: '', status: defaultStatus });
  const [contentSearchIndex, setContentSearchIndex] = useState<Record<number, string>>({});
  const [advancedSearchLoading, setAdvancedSearchLoading] = useState(false);

  // Recover { cleanHtml, signature config } from the stored body so the
  // off-screen editor renders exactly what the author saw in the draft editor
  // (same signature placement / mark-on-all-pages metadata).
  const pdfRenderExtract = useMemo(
    () => (pdfRenderData ? extractSignaturePlacementFromBody(pdfRenderData.body_html) : null),
    [pdfRenderData],
  );
  const previewRenderExtract = useMemo(
    () => (previewRenderData ? extractSignaturePlacementFromBody(previewRenderData.body_html) : null),
    [previewRenderData],
  );

  const buildQrFromToken = useCallback(async (token: string): Promise<string | null> => {
    try {
      const qrUrl = buildPublicQrUrl(token);
      return await QRCode.toDataURL(qrUrl, {
        errorCorrectionLevel: 'M',
        type: 'image/png',
        quality: 0.95,
        margin: 0,
        width: 260,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const token = String(selectedLetter?.qr_code_token || '').trim();
    if (selectedLetter?.qr_code_data) {
      setSelectedLetterQrFallback(null);
      return;
    }
    if (!token) {
      setSelectedLetterQrFallback(null);
      return;
    }

    let cancelled = false;
    void buildQrFromToken(token).then((dataUrl) => {
      if (!cancelled) setSelectedLetterQrFallback(dataUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [buildQrFromToken, selectedLetter?.qr_code_data, selectedLetter?.qr_code_token]);

  const formatDate = (d: Date) => d.toISOString().slice(0, 10);

  const applyDatePreset = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      setFilters((prev) => ({ ...prev, fromDate: prev.fromDate || '', toDate: prev.toDate || '' }));
      return;
    }

    if (preset === 'all_time') {
      // Clear the date bounds entirely so the server returns letters from
      // every date (no letter_date >= / <= clause is added).
      setFilters((prev) => ({ ...prev, fromDate: '', toDate: '' }));
      return;
    }

    const now = new Date();
    const toDate = formatDate(now);
    const from = new Date(now);
    from.setDate(now.getDate() - (preset === 'last_7_days' ? 6 : 29));
    const fromDate = formatDate(from);
    setFilters((prev) => ({ ...prev, fromDate, toDate }));
  };

  useEffect(() => {
    if (isDraftsPage) {
      setFilters((prev) => ({ ...prev, status: '' }));
      return;
    }
    if (defaultStatus) {
      setFilters((prev) => ({ ...prev, status: defaultStatus }));
    }
  }, [defaultStatus, isDraftsPage]);

  const effectiveStatusFilter = isDraftsPage ? filters.status : filters.status;

  useEffect(() => {
    applyDatePreset('all_time');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleLetters = useMemo(() => {
    // The Drafts workspace is personal: only the current user's own drafts and
    // rejected letters that need their attention are listed there.
    if (isDraftsPage) {
      const mine = letters.filter((letter) => String(letter.created_by) === String(user?.id));
      if (!effectiveStatusFilter) {
        return mine.filter((letter) => letter.status === 'draft' || letter.status === 'rejected');
      }
      return mine.filter((letter) => letter.status === effectiveStatusFilter);
    }
    // Every other page lists all letters (serial-number wise from the API);
    // opening a letter is still gated to the creator / approver.
    if (!effectiveStatusFilter) return letters;
    return letters.filter((letter) => letter.status === effectiveStatusFilter);
  }, [effectiveStatusFilter, isDraftsPage, letters, user?.id]);

  const permissions = user?.permissions;
  const canDelete = !!(permissions?.can_delete_letters || permissions?.is_admin);

  const isDeletionLocked = (letter: LetterSummary) =>
    letter.status === 'approved' ||
    !!letter.approved_at ||
    !!letter.approved_by ||
    !!String(letter.approved_signature_html || '').trim();

  const getTags = (tags: LetterSummary['tags']) => {
    if (!tags) return [] as string[];
    if (Array.isArray(tags)) return tags.filter(Boolean);
    if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch {
        return tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);
      }
    }
    return [] as string[];
  };

  const userOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    letters.forEach((letter) => {
      map.set(String(letter.created_by), { id: String(letter.created_by), name: letter.created_by_name || letter.created_by_email });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [letters]);

  useEffect(() => {
    const loadLetters = async () => {
      try {
        setLoading(true);
        setListError('');
        const response = await lettersApi.list({ ...filters, status: effectiveStatusFilter, search: '' });
        setLetters(response.data || []);
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to load letters.';
        setListError(msg);
        setLetters([]);
      } finally {
        setLoading(false);
      }
    };

    loadLetters();
  }, [effectiveStatusFilter, filters.fromDate, filters.toDate, filters.userId]);

  const normalizedAdvancedQuery = useMemo(
    () => filters.search.toLowerCase().trim(),
    [filters.search],
  );

  useEffect(() => {
    const loadContentIndex = async () => {
      if (!advancedSearchEnabled || !normalizedAdvancedQuery) {
        setAdvancedSearchLoading(false);
        return;
      }

      const targets = visibleLetters.filter((letter) => contentSearchIndex[letter.id] === undefined);
      if (!targets.length) {
        setAdvancedSearchLoading(false);
        return;
      }

      try {
        setAdvancedSearchLoading(true);
        const entries = await Promise.all(
          targets.map(async (letter) => {
            try {
              const response = await lettersApi.get(letter.id);
              const bodyHtml = String(response.data?.body_html || '');
              const text = bodyHtml
                .replace(/<style[\s\S]*?<\/style>/gi, ' ')
                .replace(/<script[\s\S]*?<\/script>/gi, ' ')
                .replace(/<[^>]+>/g, ' ')
                .replace(/&nbsp;/gi, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .toLowerCase();
              return [letter.id, text] as const;
            } catch {
              return [letter.id, ''] as const;
            }
          }),
        );

        setContentSearchIndex((prev) => {
          const next = { ...prev };
          entries.forEach(([id, text]) => {
            next[id] = text;
          });
          return next;
        });
      } finally {
        setAdvancedSearchLoading(false);
      }
    };

    loadContentIndex();
  }, [advancedSearchEnabled, normalizedAdvancedQuery, visibleLetters, contentSearchIndex]);

  const matchesTokenized = (query: string, candidate: string) => {
    if (!query) return true;
    const q = query.toLowerCase().trim();
    const c = candidate.toLowerCase();
    if (!q) return true;
    if (c.includes(q)) return true;

    const queryTokens = q.split(/\s+/).filter(Boolean);
    const candidateNumbers = c.match(/\d+/g) || [];

    return queryTokens.every((token) => {
      if (c.includes(token)) return true;
      if (/^\d+$/.test(token)) {
        return candidateNumbers.some((num) => num.includes(token) || token.includes(num));
      }
      return false;
    });
  };

  const filteredLetters = useMemo(() => {
    if (!filters.search.trim()) return visibleLetters;

    const query = filters.search.toLowerCase().trim();
    return visibleLetters.filter((letter) => {
      const tagText = getTags(letter.tags).join(' ').toLowerCase();
      const basicTarget = [
        letter.subject,
        letter.letter_number,
        letter.file_name,
        letter.created_by_name,
        letter.created_by_email,
        letter.rejection_remark,
        tagText,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (matchesTokenized(query, basicTarget)) return true;
      if (!advancedSearchEnabled) return false;

      const contentTarget = contentSearchIndex[letter.id] || '';
      return matchesTokenized(query, contentTarget);
    });
  }, [filters.search, visibleLetters, advancedSearchEnabled, contentSearchIndex]);

  const generateLetterPdfFile = async (letter: LetterSummary, options: { printSafe?: boolean } = {}) => {
    const safeName = (letter.file_name || letter.letter_number || `letter-${letter.id}`)
      .replace(/[^a-z0-9._-]+/gi, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    const fileName = `${safeName || `letter-${letter.id}`}.pdf`;
    const detailResponse = await lettersApi.get(letter.id);
    const detail: LetterDetail = detailResponse.data;
    if (!detail.qr_code_data && detail.qr_code_token) {
      const generatedQr = await buildQrFromToken(detail.qr_code_token);
      if (generatedQr) detail.qr_code_data = generatedQr;
    }

    setPdfRenderData(detail);

    // PDF-upload letters are full-page rasterised images wrapped in
    // .sepl-pdf-page blocks. The Tiptap editor engine (primary path below) has
    // no concept of those fixed page units, so it re-paginates the images by its
    // own A4 height logic and injects blank leading/trailing pages. LetterDocument
    // keeps exactly one rendered page per .sepl-pdf-page, so skip the editor path
    // and use the LetterDocument capture below for these letters.
    const isPdfUploadLetter = /sepl-pdf-page/i.test(detail.body_html || '');

    // PRIMARY PATH: render through the SAME editor engine the draft uses, so the
    // exported PDF matches the live editor exactly (table columns/alignment,
    // signature box position, "mark signature on all pages"). The off-screen
    // <NewLetterEditor> (driven by pdfRenderData) registers its exporter on
    // viewerPdfDownloaderRef; we ask it for the raw Blob and wrap it in a File.
    if (!isPdfUploadLetter) {
      try {
        const downloader = await waitForPdfDownloader(viewerPdfDownloaderRef);
        const blob = await downloader('blob', options.printSafe ? { printSafeMarginMm: 0 } : undefined);
        if (blob instanceof Blob && blob.size > 0) {
          const pdfFile = new File([blob], fileName, { type: 'application/pdf', lastModified: Date.now() });
          return { pdfFile, fileName };
        }
        throw new Error('Editor export returned an empty PDF.');
      } catch (editorErr) {
        // FALLBACK: never hard-fail a download. Fall back to the legacy
        // LetterDocument + html2canvas capture below (kept intact). Worst case the
        // output is exactly what users got before this change.
        // eslint-disable-next-line no-console
        console.warn('Editor-accurate PDF export failed; using legacy renderer.', editorErr);
      }
    }

    const waitForPages = (): Promise<Element[]> =>
      new Promise((resolve) => {
        const deadline = Date.now() + 3000;
        const poll = () => {
          const els = pdfRenderRef.current
            ? Array.from(pdfRenderRef.current.querySelectorAll('.sepl-pdf-capture-page'))
            : [];
          if (els.length > 0 || Date.now() > deadline) { resolve(els); return; }
          requestAnimationFrame(poll);
        };
        requestAnimationFrame(poll);
      });

    const pages = await waitForPages();
    await new Promise((resolve) => setTimeout(resolve, 400));

    if (pages.length <= 0) {
      throw new Error('Could not render page content.');
    }

    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas'),
    ]);
    const MAX_PDF_SIZE_BYTES = 2 * 1024 * 1024;
    const A4_W_PT = 595.28;
    const A4_H_PT = 841.89;
    const PRINT_SAFE_MARGIN_PT = 0;
    const PDF_RENDER_W_PT = A4_W_PT - (PRINT_SAFE_MARGIN_PT * 2);
    const PDF_RENDER_H_PT = A4_H_PT;
    const PDF_RENDER_X_PT = PRINT_SAFE_MARGIN_PT;
    const PDF_RENDER_Y_PT = 0;

    const buildPdfBytes = async (imageQuality: number) => {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
      let first = true;
      for (const page of pages) {
        const canvas = await html2canvas(page as HTMLElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: 794,
          height: 1122,
          windowWidth: 794,
          windowHeight: 1122,
          x: 0,
          y: 0,
          scrollX: 0,
          scrollY: 0,
        });
        if (!first) pdf.addPage('a4', 'portrait');
        pdf.addImage(
          canvas.toDataURL('image/jpeg', imageQuality),
          'JPEG',
          PDF_RENDER_X_PT,
          PDF_RENDER_Y_PT,
          PDF_RENDER_W_PT,
          PDF_RENDER_H_PT,
          undefined,
          'MEDIUM'
        );
        first = false;
      }

      const pdfBytes = pdf.output('arraybuffer');
      return new Uint8Array(pdfBytes);
    };

    const compressionAttempts = [0.88, 0.84, 0.8, 0.76, 0.72, 0.68, 0.62, 0.58, 0.54];

    let pdfBinary = new Uint8Array();
    for (const quality of compressionAttempts) {
      pdfBinary = await buildPdfBytes(quality);
      if (pdfBinary.length <= MAX_PDF_SIZE_BYTES) {
        break;
      }
    }

    const safeFileName = fileName || `letter-${letter.id}.pdf`;
    if (!pdfBinary.length) {
      throw new Error('Generated PDF is empty.');
    }
    const pdfFile = new File([pdfBinary], safeFileName, { type: 'application/pdf', lastModified: Date.now() });
    return { pdfFile, fileName };
  };

  const buildLetterShareLink = (letterId: number) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://letter.surbhi.net';
    return buildLetterRouteUrl(origin, 'letters-view', { letterId });
  };

  const preparePdfFileFromUrl = async (pdfUrl: string, preferredFileName = 'testnew.pdf'): Promise<File> => {
    const response = await fetch(pdfUrl, { method: 'GET', cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF (${response.status}).`);
    }

    const blob = await response.blob();
    if (!blob || blob.size <= 0) {
      throw new Error('Fetched PDF blob is empty.');
    }

    const cleanName = String(preferredFileName || '').trim() || 'testnew.pdf';
    const finalName = cleanName.toLowerCase().endsWith('.pdf') ? cleanName : `${cleanName}.pdf`;
    const pdfType = 'application/pdf';
    const file = new File([blob], finalName, { type: pdfType, lastModified: Date.now() });

    if (!file.name || !file.name.trim()) {
      throw new Error('Prepared file name is empty.');
    }
    if (file.size <= 0) {
      throw new Error('Prepared file size is zero.');
    }

    return file;
  };

  const executeNativeShare = async (file: File, subjectLine: string, shareText: string) => {
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean; share?: (data: ShareData) => Promise<void> };
    if (!nav.share) {
      return { shared: false, reason: 'native-share-unavailable' as const };
    }

    const sharePayload: ShareData = {
      title: subjectLine,
      text: shareText,
      files: [file],
    };

    const canShareFiles = nav.canShare?.(sharePayload) ?? false;
    if (!canShareFiles) {
      return { shared: false, reason: 'file-share-not-supported' as const };
    }

    await nav.share(sharePayload);
    return { shared: true as const };
  };

  const handleDesktopShareFallback = (file: File, subjectLine: string, shareLink: string) => {
    const body = encodeURIComponent(`Please review this letter.\n\nOpen: ${shareLink}`);
    const subject = encodeURIComponent(subjectLine || 'SEPL Letter');
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, '_self');

    const blobUrl = URL.createObjectURL(file);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = file.name || 'testnew.pdf';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
  };

  const handlePrintLetterPreview = async (letter: LetterDetail) => {
    let printIframe: HTMLIFrameElement | null = null;
    try {
      setShareError('');
      setPrintingLetterId(letter.id);

      // Use the SAME renderer as the download so the printout is identical to
      // the downloaded PDF (no print-safe shrink).
      const { pdfFile } = await generateLetterPdfFile(letter);

      // Rasterise the generated PDF to one image per page with pdf.js, then
      // print those images through an HTML document with `@page { margin: 0 }`.
      // This bypasses Chrome's PDF-viewer "Fit to printable area" scaling — the
      // thing that centres the page and adds the extra space after the footer —
      // so the printout matches the downloaded PDF exactly.
      const data = await pdfFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data }).promise;
      const pageImages: string[] = [];
      for (let pageNo = 1; pageNo <= pdf.numPages; pageNo++) {
        // eslint-disable-next-line no-await-in-loop
        const page = await pdf.getPage(pageNo);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        // eslint-disable-next-line no-await-in-loop
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
        pageImages.push(canvas.toDataURL('image/jpeg', 0.95));
      }

      if (pageImages.length === 0) {
        throw new Error('No printable pages were produced.');
      }

      const safeTitle = (letter.subject || letter.letter_number || 'letter').replace(/[<>&"]/g, ' ');
      const imagesHtml = pageImages.map((src) => `<img src="${src}" alt="" />`).join('');
      // Pages are A4; print them full-bleed. width:100% + height:auto preserves
      // the true A4 aspect (no stretch); one image per sheet via page-break.
      const printHtml = `<!doctype html><html><head><meta charset="utf-8"><title>${safeTitle}</title><style>`
        + '@page { size: A4; margin: 0; }'
        + 'html, body { margin: 0; padding: 0; background: #fff; }'
        + 'img { display: block; width: 100%; height: auto; }'
        + 'img + img { page-break-before: always; }'
        + `</style></head><body>${imagesHtml}</body></html>`;

      printIframe = document.createElement('iframe');
      printIframe.setAttribute('aria-hidden', 'true');
      printIframe.style.position = 'fixed';
      printIframe.style.right = '0';
      printIframe.style.bottom = '0';
      printIframe.style.width = '0';
      printIframe.style.height = '0';
      printIframe.style.border = '0';
      printIframe.style.visibility = 'hidden';
      document.body.appendChild(printIframe);

      const iframe = printIframe;
      await new Promise<void>((resolve) => {
        iframe.onload = () => resolve();
        iframe.srcdoc = printHtml;
      });

      const printWindow = iframe.contentWindow;
      if (!printWindow) {
        throw new Error('Print frame is unavailable.');
      }

      // Let the images lay out before invoking the print dialog.
      await new Promise((resolve) => setTimeout(resolve, 300));

      const removeIframe = () => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      };
      printWindow.onafterprint = () => window.setTimeout(removeIframe, 500);
      // Fallback cleanup in case onafterprint never fires.
      window.setTimeout(removeIframe, 60000);

      printWindow.focus();
      printWindow.print();
    } catch (error) {
      console.error('Print letter failed', error);
      setShareError('Failed to prepare printable letter. Please try again.');
      if (printIframe && printIframe.parentNode) {
        printIframe.parentNode.removeChild(printIframe);
      }
    } finally {
      setPrintingLetterId(null);
      // Unmount the off-screen renderer(s) once the printable blob is captured.
      setPdfRenderData(null);
    }
  };

  const triggerBlobDownload = (blob: Blob, downloadName: string) => {
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = downloadName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
  };

  const getLetterBaseName = (letter: LetterSummary) => (
    (letter.file_name || letter.letter_number || `letter-${letter.id}`)
      .replace(/[^a-z0-9._-]+/gi, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '') || `letter-${letter.id}`
  );

  const getUploadedFilesForLetter = async (letter: LetterSummary) => {
    const uploadsResponse = await lettersApi.listUploads(letter.id).catch(() => ({ data: [] as any[] }));
    const uploads = Array.isArray(uploadsResponse?.data) ? uploadsResponse.data : [];

    if (uploads.length > 0) {
      const downloaded = await Promise.allSettled(
        uploads.map(async (upload: any, index: number) => {
          const uploadRes = await lettersApi.downloadUpload(letter.id, upload.id);
          const uploadBlob = uploadRes?.data instanceof Blob
            ? uploadRes.data
            : new Blob([uploadRes?.data], { type: 'application/pdf' });
          const rawName = String(upload?.file_name || `uploaded-${index + 1}.pdf`);
          const safeName = rawName.toLowerCase().endsWith('.pdf') ? rawName : `${rawName}.pdf`;
          return { blob: uploadBlob, name: safeName };
        }),
      );

      let failedUploads = 0;
      const files: Array<{ blob: Blob; name: string }> = [];
      downloaded.forEach((result) => {
        if (result.status === 'fulfilled') files.push(result.value);
        else failedUploads += 1;
      });

      if (failedUploads > 0) {
        setShareError(`${failedUploads} uploaded PDF(s) could not be downloaded.`);
      }

      return files;
    }

    if (letter.mapped_pdf_name) {
      const mappedRes = await lettersApi.downloadMappedPdf(letter.id);
      const mappedBlob = mappedRes.data as Blob;
      const mappedName = String(letter.mapped_pdf_name || `mapped-${letter.id}.pdf`);
      return [{
        blob: mappedBlob,
        name: mappedName.toLowerCase().endsWith('.pdf') ? mappedName : `${mappedName}.pdf`,
      }];
    }

    return [] as Array<{ blob: Blob; name: string }>;
  };

  const downloadAsSingleOrZip = async (
    letter: LetterSummary,
    files: Array<{ blob: Blob; name: string }>,
    zipSuffix = 'bundle',
  ) => {
    if (!files.length) {
      throw new Error('No files available for download.');
    }

    if (files.length === 1) {
      triggerBlobDownload(files[0].blob, files[0].name);
      return;
    }

    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    files.forEach((file) => zip.file(file.name, file.blob));
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    triggerBlobDownload(zipBlob, `${getLetterBaseName(letter)}_${zipSuffix}.zip`);
  };

  const downloadLetterPdf = async (letter: LetterSummary, mode: 'letter' | 'uploaded' | 'all') => {
    setShareError('');
    setUploadSuccess('');
    setDownloadingLetterId(letter.id);
    setOpenDownloadMenuId(null);
    try {
      const { pdfFile, fileName } = await generateLetterPdfFile(letter);

      if (mode === 'letter') {
        triggerBlobDownload(pdfFile, fileName);
      } else if (mode === 'uploaded') {
        const uploadedFiles = await getUploadedFilesForLetter(letter);
        await downloadAsSingleOrZip(letter, uploadedFiles, 'uploads');
      } else {
        const uploadedFiles = await getUploadedFilesForLetter(letter);
        const allFiles: Array<{ blob: Blob; name: string }> = [
          { blob: pdfFile, name: fileName },
          ...uploadedFiles,
        ];
        await downloadAsSingleOrZip(letter, allFiles, 'bundle');
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error;
      if (status === 403) setShareError(`PDF download blocked (403): ${serverMsg || 'You do not have permission to access this letter.'}`);
      else if (status === 401) setShareError('PDF download blocked (401): Please log in again and retry.');
      else setShareError(`PDF download failed: ${serverMsg || err?.message || 'Unknown error.'}`);
    } finally {
      setDownloadingLetterId(null);
      setPdfRenderData(null);
    }
  };

  const shareLetter = async (letter: LetterSummary) => {
    setShareError('');
    setSharingLetterId(letter.id);
    const shareLink = buildLetterShareLink(letter.id);
    const subjectLine = letter.subject || letter.file_name || 'SEPL Letter';
    const shareText = `Please review this letter.\n\nSubject: ${subjectLine}\nOpen: ${shareLink}`;

    try {
      const { pdfFile, fileName } = await generateLetterPdfFile(letter);

      if (!pdfFile.name || pdfFile.size <= 0) {
        setShareError('Share failed: generated attachment is invalid.');
        return;
      }

      // Force URL -> Blob -> File path for better compatibility with desktop share targets.
      const transientUrl = URL.createObjectURL(pdfFile);
      let preparedFile: File;
      try {
        preparedFile = await preparePdfFileFromUrl(transientUrl, fileName || pdfFile.name || 'testnew.pdf');
      } finally {
        URL.revokeObjectURL(transientUrl);
      }

      if (!preparedFile.name || preparedFile.size <= 0) {
        setShareError('Share failed: prepared file is invalid (empty name/size).');
        return;
      }

      try {
        const shareResult = await executeNativeShare(preparedFile, subjectLine, shareText);
        if (shareResult.shared) return;

        handleDesktopShareFallback(preparedFile, subjectLine, shareLink);
        setShareError('Desktop share target does not support file attachments here. Mail composer opened and PDF downloaded.');
        return;
      } catch (shareErr: any) {
        if (shareErr?.name === 'AbortError') return;
        handleDesktopShareFallback(preparedFile, subjectLine, shareLink);
        setShareError('Outlook/Gmail desktop rejected browser file share (empty/no_name). Mail composer opened and PDF downloaded for manual attachment.');
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error;
      if (status === 403) setShareError(`Share blocked (403): ${serverMsg || 'You do not have permission to access this letter.'}`);
      else if (status === 401) setShareError('Share blocked (401): Please log in again and retry.');
      else setShareError(`Share failed: ${serverMsg || err?.message || 'Unknown error.'}`);
    } finally {
      setSharingLetterId(null);
      setPdfRenderData(null);
    }
  };

  const openLetter = useCallback(async (letterId: number) => {
    try {
      setLoadingDetail(true);
      setDetailError('');
      setAccessPopupMessage('');
      setActiveUploadTabId(null);
      const [detailResponse, uploadsResponse] = await Promise.all([
        lettersApi.get(letterId, { suppressGlobalError: true }),
        lettersApi.listUploads(letterId, { suppressGlobalError: true }).catch(() => null),
      ]);

      const detail = detailResponse.data as LetterDetail;
      const uploads = Array.isArray(uploadsResponse?.data) ? uploadsResponse.data : null;

      setSelectedLetter(normalizeLetterDetailUploads(detail, uploads));
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setSelectedLetter(null);
        setAccessPopupMessage("You don't have access to this letter.");
      } else {
        const message = err?.response?.data?.error || err?.message || 'Failed to load letter details.';
        setDetailError(message);
      }
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    const letterIdParam = searchParams.get('letterId');
    if (!letterIdParam) return;

    const parsedId = Number(letterIdParam);
    if (!Number.isFinite(parsedId) || parsedId <= 0) return;

    openLetter(parsedId);
    const params = new URLSearchParams(searchParams);
    params.delete('letterId');
    setSearchParams(params, { replace: true });
  }, [openLetter, searchParams, setSearchParams]);

  useEffect(() => {
    let isCancelled = false;
    let objectUrl = '';

    if (!selectedLetter?.id || activeUploadTabId === null) {
      setActiveUploadPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      setActiveUploadPreviewLoading(false);
      setActiveUploadPreviewError('');
      return;
    }

    const loadUploadPreview = async () => {
      try {
        setActiveUploadPreviewLoading(true);
        setActiveUploadPreviewError('');

        const response = await lettersApi.downloadUpload(selectedLetter.id, activeUploadTabId);
        const blob = response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data], { type: 'application/pdf' });

        objectUrl = URL.createObjectURL(blob);
        if (isCancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        setActiveUploadPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return objectUrl;
        });
      } catch (err: any) {
        if (!isCancelled) {
          setActiveUploadPreviewError(err?.response?.data?.error || err?.message || 'Failed to load uploaded PDF preview.');
          setActiveUploadPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return '';
          });
        }
      } finally {
        if (!isCancelled) setActiveUploadPreviewLoading(false);
      }
    };

    loadUploadPreview();

    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [activeUploadTabId, selectedLetter?.id]);

  // Editor-accurate preview: when the "Letter Content" tab is active, render the
  // selected letter through the SAME engine the download uses and display the
  // resulting PDF in an iframe — so the on-screen preview is byte-identical to
  // the downloaded file (fixes table overlap / signature placement that the
  // legacy LetterDocument renderer got wrong). Falls back to <LetterDocument>
  // on any error so the preview never goes blank.
  useEffect(() => {
    if (!selectedLetter?.id || activeUploadTabId !== null) {
      setPreviewRenderData(null);
      setPreviewPdfLoading(false);
      setPreviewPdfFailed(false);
      setPreviewPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ''; });
      return;
    }

    let cancelled = false;
    let createdUrl = '';

    setPreviewPdfFailed(false);
    setPreviewPdfLoading(true);
    setPreviewPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ''; });

    // PDF-upload letters (full-page images in .sepl-pdf-page blocks) must render
    // through LetterDocument, which keeps one page per uploaded PDF page. The
    // Tiptap editor preview re-paginates those images and inserts blank leading/
    // trailing pages, so skip it and show the LetterDocument fallback directly.
    if (/sepl-pdf-page/i.test(selectedLetter.body_html || '')) {
      setPreviewPdfLoading(false);
      setPreviewPdfFailed(true);
      return;
    }

    setPreviewRenderData(selectedLetter);

    (async () => {
      try {
        const downloader = await waitForPdfDownloader(previewPdfDownloaderRef);
        const blob = await downloader('blob');
        if (cancelled) return;
        if (!(blob instanceof Blob) || blob.size <= 0) {
          throw new Error('Editor preview returned an empty PDF.');
        }
        createdUrl = URL.createObjectURL(blob);
        setPreviewPdfUrl(createdUrl);
      } catch (previewErr) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('Editor-accurate preview failed; showing fallback.', previewErr);
        setPreviewPdfFailed(true);
      } finally {
        if (!cancelled) {
          setPreviewPdfLoading(false);
          // Unmount the heavy off-screen editor; the blob URL keeps the preview.
          setPreviewRenderData(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [selectedLetter, activeUploadTabId]);

  const goToEditor = (letterId?: number) => {
    if (letterId) {
      const target = letters.find((letter) => letter.id === letterId);
      if (target && target.status !== 'draft' && target.status !== 'rejected') {
        openLetter(letterId);
        return;
      }
    }
    const params = new URLSearchParams();
    params.set('page', 'letters-create');
    if (letterId) params.set('letterId', String(letterId));
    setSearchParams(params);
  };

  const goHome = () => {
    const params = new URLSearchParams();
    params.set('page', 'letters-dashboard');
    setSearchParams(params);
  };

  const [recallingLetterId, setRecallingLetterId] = useState<number | null>(null);
  const handleRecall = async (letter: { id: number; subject?: string; file_name?: string | null }) => {
    const ok = window.confirm(
      `Recall "${letter.file_name || letter.subject || 'this letter'}" from approval?\n\nIt will be removed from the signatory's queue and open in the editor as a draft. After your changes, click "Submit for Signature" again.`,
    );
    if (!ok) return;
    try {
      setRecallingLetterId(letter.id);
      setDeleteError('');
      await lettersApi.recall(letter.id);
      const params = new URLSearchParams();
      params.set('page', 'letters-create');
      params.set('letterId', String(letter.id));
      setSearchParams(params);
    } catch (err: any) {
      setDeleteError(err?.response?.data?.error || err?.message || 'Failed to recall letter.');
      window.setTimeout(() => setDeleteError(''), 5000);
      try {
        const response = await lettersApi.list({ ...filters, search: '' });
        setLetters(response.data || []);
      } catch { /* ignore */ }
    } finally {
      setRecallingLetterId(null);
    }
  };

  const handleDelete = async (letterId: number) => {
    if (!canDelete) return;
    const targetLetter = letters.find((letter) => letter.id === letterId);
    if (targetLetter && isDeletionLocked(targetLetter)) {
      setDeleteError('Approved letters cannot be deleted.');
      window.setTimeout(() => setDeleteError(''), 5000);
      return;
    }
    if (!window.confirm('Delete this letter? This action cannot be undone.')) return;
    try {
      setDeleteError('');
      await lettersApi.delete(letterId);
      if (selectedLetter?.id === letterId) setSelectedLetter(null);
      const response = await lettersApi.list({ ...filters, search: '' });
      setLetters(response.data || []);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to delete letter.';
      setDeleteError(msg);
      window.setTimeout(() => setDeleteError(''), 5000);
    }
  };

  const openMappedPdfPicker = (letterId: number) => {
    setUploadTargetLetterId(letterId);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
      uploadInputRef.current.click();
    }
  };

  const handleMappedPdfFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !uploadTargetLetterId) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setShareError('Only PDF files are allowed for receiving letter upload.');
      return;
    }

    try {
      setShareError('');
      setUploadSuccess('');
      setUploadingLetterId(uploadTargetLetterId);
      const response = await lettersApi.uploadMappedPdf(uploadTargetLetterId, file);
      const updated = normalizeLetterDetailUploads(response?.data as LetterDetail);
      const nextUploadCount =
        typeof updated?.upload_count === 'number'
          ? updated.upload_count
          : Array.isArray(updated?.pdf_uploads)
            ? updated.pdf_uploads.length
            : null;

      // Update only the affected row so the uploads count refreshes immediately.
      setLetters((prevLetters) =>
        prevLetters.map((letter) =>
          letter.id === uploadTargetLetterId
            ? {
                ...letter,
                mapped_pdf_name: updated.mapped_pdf_name ?? letter.mapped_pdf_name,
                mapped_pdf_uploaded_at: updated.mapped_pdf_uploaded_at ?? letter.mapped_pdf_uploaded_at,
                upload_count: nextUploadCount ?? Math.max(1, Number(letter.upload_count || 0) + 1),
              }
            : letter
        )
      );

      if (selectedLetter?.id === uploadTargetLetterId) {
        setSelectedLetter(updated);
      }
      setUploadSuccess(`✓ Receiving letter uploaded successfully for letter ${updated.letter_number || updated.id}.`);
      window.setTimeout(() => setUploadSuccess(''), 3000);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to upload receiving letter.';
      setShareError(msg);
    } finally {
      setUploadingLetterId(null);
      setUploadTargetLetterId(null);
    }
  };

  // Close download menu on click outside or scroll
  useEffect(() => {
    if (openDownloadMenuId === null) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-download-menu]') || target.closest('[data-download-btn]')) return;
      setOpenDownloadMenuId(null);
      setDownloadMenuPos(null);
    };
    const closeMenu = () => { setOpenDownloadMenuId(null); setDownloadMenuPos(null); };
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [openDownloadMenuId]);

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6">
      <input
        ref={uploadInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleMappedPdfFileChange}
      />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <button onClick={goHome} className="inline-flex items-center gap-2 text-violet-700 hover:text-violet-800 font-medium mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to Letters Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">{pageTitle}</h1>
          <p className="text-gray-600 mt-2">{pageTitle === 'View Drafts' ? 'Draft and rejected letters that can be corrected and submitted again.' : 'Search, filter, review, and export official letters.'}</p>
        </div>
        <button onClick={() => goToEditor()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white">
          <Plus className="w-4 h-4" /> New Letter
        </button>
      </div>

      {listError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-red-700 text-sm font-medium">
          ⚠ {listError}
        </div>
      )}
      {deleteError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-red-700 text-sm font-medium">
          ⚠ {deleteError}
        </div>
      )}
      {shareError && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 text-amber-800 text-sm font-medium flex items-start justify-between gap-3">
          <span className="whitespace-pre-wrap break-words">{shareError}</span>
          <button onClick={() => setShareError('')} className="flex-shrink-0 text-amber-600 hover:text-amber-900 font-bold text-base leading-none mt-0.5">✕</button>
        </div>
      )}
      {uploadSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-3 text-green-700 text-sm font-medium flex items-start justify-between gap-3">
          <span className="whitespace-pre-wrap break-words">{uploadSuccess}</span>
          <button onClick={() => setUploadSuccess('')} className="flex-shrink-0 text-green-600 hover:text-green-900 font-bold text-base leading-none mt-0.5">✕</button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <input value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} placeholder="Subject, letter number, user, or tags" className="w-full rounded-lg border border-gray-300 pl-10 pr-3 py-2" />
          </div>
          <div className="mt-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={advancedSearchEnabled}
                onChange={(e) => setAdvancedSearchEnabled(e.target.checked)}
              />
              <span>Advanced Search (scan letter content)</span>
            </label>
            {advancedSearchEnabled ? (
              <p className="text-xs text-gray-500 mt-1">Advanced search finds approximate text in letter body content.</p>
            ) : null}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Date Filter</label>
          <select
            value={datePreset}
            onChange={(e) => {
              const preset = e.target.value as DateRangePreset;
              setDatePreset(preset);
              applyDatePreset(preset);
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          >
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_month">Last Month</option>
            <option value="all_time">All Time</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
            value={filters.status}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, status: e.target.value }));
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          >
            {isDraftsPage ? (
              <>
                <option value="">Draft + Rejected</option>
                <option value="draft">Draft</option>
                <option value="rejected">Rejected</option>
              </>
            ) : (
              <>
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </>
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Created By</label>
          <select value={filters.userId} onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
            <option value="">All users</option>
            {userOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
        </div>
        {datePreset === 'custom' ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
              <input type="date" value={filters.fromDate} onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
              <input type="date" value={filters.toDate} onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
            </div>
          </>
        ) : null}
      </div>

      {advancedSearchLoading ? (
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-5 py-3 text-violet-700 text-sm font-medium">
          Advanced search is scanning letter content...
        </div>
      ) : null}

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Letter</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">File Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tags</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Created By</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading letters...</td></tr>
                ) : listError ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-red-500">⚠ {listError}</td></tr>
                ) : filteredLetters.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No letters found for the selected filters.</td></tr>
                ) : (
                  filteredLetters.map((letter) => (
                    <tr key={letter.id} className="hover:bg-violet-50 transition">
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() => openLetter(letter.id)}
                          className={`font-semibold text-left hover:underline ${letter.letter_number ? 'text-gray-900' : 'text-amber-600 italic'}`}
                        >
                          {letter.letter_number ? formatSeplReferenceNumber(letter.letter_number) : 'Pending Approval'}
                        </button>
                        <div className="text-xs text-gray-500">{formatDateTime(letter.letter_date)}</div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 max-w-[150px] truncate">
                        {letter.file_name ? (
                          <button
                            type="button"
                            onClick={() => openLetter(letter.id)}
                            className="text-violet-700 hover:text-violet-800 hover:underline text-left truncate max-w-[150px]"
                            title={letter.file_name}
                          >
                            {letter.file_name}
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 max-w-[220px]">
                        {getTags(letter.tags).length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {getTags(letter.tags).map((tag, index) => (
                              <span key={`${letter.id}-tag-${index}`} className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 text-xs font-medium">
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">{letter.created_by_name}</td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${statusClassMap[letter.status]}`}>{statusLabelMap[letter.status]}</span>
                        {letter.status === 'rejected' ? (
                          <div className="mt-2 max-w-[280px] text-xs text-rose-700 whitespace-pre-wrap break-words">
                            <span className="font-semibold">Remark:</span> {formatRejectionRemark(letter.rejection_remark) || 'No remark found'}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2">
                          {letter.status === 'draft' || letter.status === 'rejected' ? (
                            <button
                              onClick={() => goToEditor(letter.id)}
                              title="Edit"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          ) : null}
                          {letter.status === 'pending_approval' && String(letter.created_by) === String(user?.id) ? (
                            <button
                              onClick={() => handleRecall(letter)}
                              disabled={recallingLetterId !== null}
                              title="Recall from approval — pull it back, edit and submit again"
                              className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-medium disabled:opacity-60"
                            >
                              {recallingLetterId === letter.id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Undo2 className="w-4 h-4" />}
                              Recall
                            </button>
                          ) : null}
                          {(() => {
                            const isDownloading = downloadingLetterId === letter.id;
                            const isSharing = sharingLetterId === letter.id;
                            const isUploadingThisLetter = uploadingLetterId === letter.id;
                            const isAnyActionBusy = downloadingLetterId !== null || sharingLetterId !== null || uploadingLetterId !== null;
                            const isDownloadMenuOpen = openDownloadMenuId === letter.id;
                            return (
                              <>
                          {letter.status === 'approved' ? (
                            <button
                              onClick={() => openMappedPdfPicker(letter.id)}
                              title={isUploadingThisLetter ? 'Uploading receiving letter…' : 'Upload Receiving Letter'}
                              disabled={isAnyActionBusy}
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 disabled:opacity-60"
                            >
                              {isUploadingThisLetter
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Plus className="w-4 h-4" />}
                            </button>
                          ) : null}
                          <div className="relative">
                            <button
                              ref={(el) => { if (isDownloadMenuOpen) downloadBtnRef.current = el; }}
                              data-download-btn
                              onClick={(e) => {
                                const next = openDownloadMenuId === letter.id ? null : letter.id;
                                setOpenDownloadMenuId(next);
                                if (next !== null) {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setDownloadMenuPos({ top: rect.bottom + 4, left: rect.right - 224 });
                                } else {
                                  setDownloadMenuPos(null);
                                }
                              }}
                              title={isDownloading ? 'Generating PDF…' : 'Download'}
                              disabled={isAnyActionBusy}
                              className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-md border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 disabled:opacity-60"
                            >
                              {isDownloading
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Download className="w-4 h-4" />}
                              <span className="text-xs">▾</span>
                            </button>
                          </div>
                          <button
                            onClick={() => shareLetter(letter)}
                            title={isSharing ? 'Generating PDF…' : 'Share'}
                            disabled={isAnyActionBusy}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 disabled:opacity-60"
                          >
                            {isSharing
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Share2 className="w-4 h-4" />}
                          </button>
                              </>
                            );
                          })()}
                          {canDelete && !isDeletionLocked(letter) && (
                            <button
                              onClick={() => handleDelete(letter.id)}
                              title="Delete"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-red-200 bg-red-50 hover:bg-red-100 text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      {detailError ? (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-red-700 text-sm font-medium">
          ⚠ {detailError}
        </div>
      ) : null}

      {accessPopupMessage ? (
        <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-amber-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-amber-100 bg-amber-50">
              <h3 className="text-base font-semibold text-amber-900">Access denied</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700">{accessPopupMessage}</p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setAccessPopupMessage('')}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Hidden off-screen container for PDF generation */}
      {pdfRenderData ? (
        <div
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: '794px',
            minWidth: '794px',
            maxWidth: '794px',
            minHeight: '1122px',
            height: 'auto',
            pointerEvents: 'none',
            opacity: 0,
            zIndex: -1,
            overflow: 'hidden',
          }}
          aria-hidden="true"
        >
          <style>{`
            .sepl-pdf-render-root,
            .sepl-pdf-render-root * {
              zoom: 1 !important;
              transform: none !important;
            }
            .sepl-pdf-render-root .sepl-pdf-capture-page {
              width: 794px !important;
              min-width: 794px !important;
              max-width: 794px !important;
              height: 1122px !important;
              min-height: 1122px !important;
              max-height: 1122px !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: hidden !important;
              flex: 0 0 auto !important;
              box-sizing: border-box !important;
            }
          `}</style>
          <div ref={pdfRenderRef} className="sepl-pdf-render-root" style={{ width: '794px', minHeight: '1122px', margin: 0, padding: 0, overflow: 'visible' }}>
            <LetterDocument
              subject={pdfRenderData.subject}
              letterDate={pdfRenderData.letter_date}
              serialNumber={pdfRenderData.serial_number}
              letterNumber={pdfRenderData.letter_number}
              bodyHtml={pdfRenderData.body_html}
              letterheadHtml={pdfRenderData.letterhead_html}
              footerHtml={pdfRenderData.footer_html}
              approvedSignatureHtml={pdfRenderData.approved_signature_html}
              qrCodeData={pdfRenderData.qr_code_data}
              exportMode
            />
          </div>
        </div>
      ) : null}

      {/* Off-screen editor-accurate renderer for DOWNLOAD / SHARE / PRINT.
          Uses the SAME engine as the draft editor so exported PDFs match the
          live editor exactly (tables, alignment, signature box, mark-all-pages).
          Registers its exporter on viewerPdfDownloaderRef; generateLetterPdfFile
          asks it for the Blob. editable={false} so it never autosaves / writes
          localStorage (keeps the user's drafts safe). */}
      {pdfRenderData && pdfRenderExtract ? (
        <div style={OFFSCREEN_EDITOR_HOST_STYLE} aria-hidden="true">
          <NewLetterEditor
            key={`download-${pdfRenderData.id}`}
            editable={false}
            content={pdfRenderExtract.cleanHtml}
            onChange={noopEditorChange}
            title={pdfRenderData.subject || pdfRenderData.file_name || `letter-${pdfRenderData.id}`}
            qrCodeData={pdfRenderData.qr_code_data || null}
            letterNumber={pdfRenderData.letter_number || null}
            letterDate={pdfRenderData.letter_date || null}
            approvedSignatureHtml={pdfRenderData.approved_signature_html || null}
            signaturePlacementConfig={pdfRenderExtract.config}
            registerPdfDownloader={(fn) => { viewerPdfDownloaderRef.current = fn; }}
          />
        </div>
      ) : null}

      {/* Off-screen editor-accurate renderer for the PREVIEW modal. Same engine;
          its generated Blob is shown in an iframe so the preview is identical to
          the downloaded file. */}
      {previewRenderData && previewRenderExtract ? (
        <div style={OFFSCREEN_EDITOR_HOST_STYLE} aria-hidden="true">
          <NewLetterEditor
            key={`preview-${previewRenderData.id}`}
            editable={false}
            content={previewRenderExtract.cleanHtml}
            onChange={noopEditorChange}
            title={previewRenderData.subject || previewRenderData.file_name || `letter-${previewRenderData.id}`}
            qrCodeData={previewRenderData.qr_code_data || selectedLetterQrFallback || null}
            letterNumber={previewRenderData.letter_number || null}
            letterDate={previewRenderData.letter_date || null}
            approvedSignatureHtml={previewRenderData.approved_signature_html || null}
            signaturePlacementConfig={previewRenderExtract.config}
            registerPdfDownloader={(fn) => { previewPdfDownloaderRef.current = fn; }}
          />
        </div>
      ) : null}

      {selectedLetter ? (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[92vh] bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Letter Preview</h3>
                <p className="text-xs text-gray-500">{selectedLetter.subject}</p>
                {selectedLetter.status === 'rejected' ? (
                  <div className="mt-2 max-w-2xl rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                    <span className="font-semibold">Rejected remark:</span> {getDetailRejectionRemark(selectedLetter) || 'No remark found'}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {activeUploadTabId === null ? (
                  <button
                    type="button"
                    onClick={() => handlePrintLetterPreview(selectedLetter)}
                    disabled={printingLetterId === selectedLetter.id}
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                    title="Print Letter"
                  >
                    {printingLetterId === selectedLetter.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Printer className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium">Print</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLetter(null);
                    setActiveUploadTabId(null);
                    setActiveUploadPreviewError('');
                  }}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="px-5 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-1 overflow-x-auto">
              <button
                onClick={() => setActiveUploadTabId(null)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition whitespace-nowrap ${
                  activeUploadTabId === null
                    ? 'bg-white text-violet-700 border border-violet-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Letter Content
              </button>
              {(selectedLetter.pdf_uploads || []).map((upload, index) => (
                <button
                  key={upload.id}
                  onClick={() => setActiveUploadTabId(upload.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition whitespace-nowrap ${
                    activeUploadTabId === upload.id
                      ? 'bg-white text-violet-700 border border-violet-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  title={upload.file_name || `Upload ${index + 1}`}
                >
                  {upload.file_name || `Upload ${index + 1}`}
                </button>
              ))}
            </div>

            <div className="p-5 overflow-y-auto bg-gray-50 flex-1">
              {loadingDetail ? (
                <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">Loading letter preview...</div>
              ) : activeUploadTabId !== null && selectedLetter.pdf_uploads ? (
                (() => {
                  const activeUpload = selectedLetter.pdf_uploads.find((u) => u.id === activeUploadTabId);
                  if (!activeUpload) {
                    return <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-red-500">Upload not found.</div>;
                  }
                  if (activeUploadPreviewLoading) {
                    return <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">Loading uploaded PDF preview...</div>;
                  }
                  if (activeUploadPreviewError) {
                    return <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-red-500">{activeUploadPreviewError}</div>;
                  }
                  if (!activeUploadPreviewUrl) {
                    return <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">Preparing uploaded PDF preview...</div>;
                  }
                  return (
                    <div className="bg-white border border-gray-200 rounded-xl p-0 overflow-hidden h-full">
                      <iframe
                        src={activeUploadPreviewUrl}
                        className="w-full h-full"
                        style={{ minHeight: '500px' }}
                        title={`PDF Upload: ${activeUpload.file_name}`}
                      />
                    </div>
                  );
                })()
              ) : previewPdfFailed ? (
                // Editor-accurate preview failed — fall back to the legacy
                // renderer so the preview is never blank.
                <LetterDocument
                  subject={selectedLetter.subject}
                  letterDate={selectedLetter.letter_date}
                  serialNumber={selectedLetter.serial_number}
                  letterNumber={selectedLetter.letter_number}
                  bodyHtml={selectedLetter.body_html}
                  letterheadHtml={selectedLetter.letterhead_html}
                  footerHtml={selectedLetter.footer_html}
                  approvedSignatureHtml={selectedLetter.approved_signature_html}
                  qrCodeData={selectedLetter.qr_code_data || selectedLetterQrFallback}
                />
              ) : previewPdfUrl ? (
                // Editor-accurate render shown as a PDF — identical to download.
                <div className="bg-white border border-gray-200 rounded-xl p-0 overflow-hidden h-full">
                  <iframe
                    src={previewPdfUrl}
                    className="w-full h-full"
                    style={{ minHeight: '70vh' }}
                    title={`Letter Preview: ${selectedLetter.subject || selectedLetter.letter_number}`}
                  />
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2 align-[-2px]" />
                  Rendering letter preview...
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Download dropdown rendered as fixed portal to escape overflow containers */}
      {openDownloadMenuId !== null && downloadMenuPos && ReactDOM.createPortal(
        <div
          data-download-menu
          className="fixed rounded-lg border border-gray-200 bg-white shadow-xl py-1"
          style={{ top: downloadMenuPos.top, left: downloadMenuPos.left, width: 224, zIndex: 9999 }}
        >
          <button
            type="button"
            onClick={() => { const l = filteredLetters.find(x => x.id === openDownloadMenuId); if (l) downloadLetterPdf(l, 'letter'); }}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
          >
            Download Created Letter
          </button>
          <button
            type="button"
            onClick={() => { const l = filteredLetters.find(x => x.id === openDownloadMenuId); if (l) downloadLetterPdf(l, 'uploaded'); }}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
          >
            Download Uploaded Letter
          </button>
          <button
            type="button"
            onClick={() => { const l = filteredLetters.find(x => x.id === openDownloadMenuId); if (l) downloadLetterPdf(l, 'all'); }}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
          >
            Download All
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}